import { categoriesApi } from '@/api/categories.api';
import { apiErrorMessage } from '@/api/client';
import { itemsApi } from '@/api/items.api';
import type { CategoryModel, CreateItem, ItemModel, UpdateItem } from '@/types/categories.types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface AdminData {
  categories: CategoryModel[];
  items: ItemModel[];
  isLoading: boolean;
  error: string | null;
  createCategory: (label: string, color: string) => Promise<CategoryModel>;
  renameCategory: (key: string, label: string) => Promise<void>;
  deleteCategory: (key: string) => Promise<void>;
  attachItems: (key: string, itemIds: string[]) => Promise<void>;
  setItemCategory: (itemId: string, key: string, member: boolean) => Promise<void>;
  createItem: (body: CreateItem) => Promise<void>;
  updateItem: (itemId: string, body: UpdateItem) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
}

const replaceItems = (current: ItemModel[], changed: ItemModel[]): ItemModel[] => {
  const byId = new Map(changed.map((item) => [item.id, item]));
  return current.map((item) => byId.get(item.id) ?? item);
};

/**
 * Categories and items for the admin screens, with every mutation the screens offer.
 *
 * Mutations update the local lists from the server's answer; the category list is re-read after
 * item changes because a custom category's "methods" label is derived from its items. A failed
 * mutation throws (the screen shows the message); the lists stay as they were.
 */
export const useAdminData = (): AdminData => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<CategoryModel[]>([]);
  const [items, setItems] = useState<ItemModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([categoriesApi.list(), itemsApi.list()])
      .then(([loadedCategories, loadedItems]) => {
        if (cancelled) return;
        setCategories(loadedCategories);
        setItems(loadedItems);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(apiErrorMessage(err, t('admin.loadFailed')));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const refreshCategories = useCallback(async () => {
    setCategories(await categoriesApi.list());
  }, []);

  const createCategory = useCallback(async (label: string, color: string) => {
    // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
    const created = await categoriesApi.create({ label_cs: label, color });
    setCategories((current) => [...current, created].sort((a, b) => a.sort_order - b.sort_order));
    return created;
  }, []);

  const renameCategory = useCallback(async (key: string, label: string) => {
    // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
    const updated = await categoriesApi.update(key, { label_cs: label });
    setCategories((current) => current.map((entry) => (entry.key === key ? updated : entry)));
  }, []);

  const deleteCategory = useCallback(async (key: string) => {
    await categoriesApi.remove(key);
    setCategories((current) => current.filter((entry) => entry.key !== key));
    setItems((current) =>
      current.map((item) =>
        item.categories.includes(key) ? { ...item, categories: item.categories.filter((k) => k !== key) } : item
      )
    );
  }, []);

  const attachItems = useCallback(
    async (key: string, itemIds: string[]) => {
      const changed = await categoriesApi.attachItems(key, itemIds);
      setItems((current) => replaceItems(current, changed));
      await refreshCategories();
    },
    [refreshCategories]
  );

  const setItemCategory = useCallback(
    async (itemId: string, key: string, member: boolean) => {
      const changed = member ? await itemsApi.addCategory(itemId, key) : await itemsApi.removeCategory(itemId, key);
      setItems((current) => replaceItems(current, [changed]));
      await refreshCategories();
    },
    [refreshCategories]
  );

  const createItem = useCallback(
    async (body: CreateItem) => {
      const created = await itemsApi.create(body);
      setItems((current) => [...current, created]);
      await refreshCategories();
    },
    [refreshCategories]
  );

  const updateItem = useCallback(
    async (itemId: string, body: UpdateItem) => {
      const changed = await itemsApi.update(itemId, body);
      setItems((current) => replaceItems(current, [changed]));
      await refreshCategories();
    },
    [refreshCategories]
  );

  const deleteItem = useCallback(
    async (itemId: string) => {
      await itemsApi.remove(itemId);
      setItems((current) => current.filter((item) => item.id !== itemId));
      await refreshCategories();
    },
    [refreshCategories]
  );

  return {
    categories,
    items,
    isLoading,
    error,
    createCategory,
    renameCategory,
    deleteCategory,
    attachItems,
    setItemCategory,
    createItem,
    updateItem,
    deleteItem,
  };
};
